import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { slackService, ReviewKudosDto, PaginatedKudosResponse } from '../services/slackService';

export const kudosKeys = {
  all: ['kudos'] as const,
  lists: () => [...kudosKeys.all, 'list'] as const,
  forUser: (userId: string) => [...kudosKeys.lists(), 'for-user', userId] as const,
  unreviewed: () => [...kudosKeys.lists(), 'unreviewed'] as const,
  nonKudos: () => [...kudosKeys.lists(), 'non-kudos'] as const,
};

export const useKudosForUser = (userId: string, page: number = 1, size: number = 20) => {
  return useQuery<PaginatedKudosResponse>({
    queryKey: [...kudosKeys.forUser(userId), page, size],
    queryFn: () => slackService.getKudosForUser(userId, page, size),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - kudos don't change frequently
    refetchOnMount: true, // Override global refetchOnMount:false to ensure kudos load on dashboard
  });
};

export const useUnreviewedKudos = (page: number = 1, size: number = 20) => {
  return useQuery<PaginatedKudosResponse>({
    queryKey: [...kudosKeys.unreviewed(), page, size],
    queryFn: () => slackService.getUnreviewedKudos(page, size),
    staleTime: 2 * 60 * 1000, // 2 minutes - admin review page needs fresher data
  });
};

export const useNonKudosMessages = (page: number = 1, size: number = 20) => {
  return useQuery<PaginatedKudosResponse>({
    queryKey: [...kudosKeys.nonKudos(), page, size],
    queryFn: () => slackService.getNonKudosMessages(page, size),
    staleTime: 2 * 60 * 1000, // 2 minutes - admin review page needs fresher data
  });
};

export const useReviewKudos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReviewKudosDto }) => slackService.reviewKudos(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kudosKeys.lists() });
    },
  });
};

export const useHideKudos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => slackService.hideKudos(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kudosKeys.lists() });
    },
  });
};

export const useDeleteKudos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => slackService.deleteKudos(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kudosKeys.lists() });
    },
  });
};
